/**
 * Register page — POST /api/auth/register via AuthContext.
 *
 * Role is determined by the URL query parameter, never by an in-form
 * picker:
 *
 *     /register                  -> participant (default) + cross-link to vendor
 *     /register?as=participant   -> participant, no cross-link
 *     /register?as=vendor        -> dealer flow (Stripe Checkout on submit)
 *
 * This was Carlton's complaint #2: "the entry link already declared the
 * role, so the form shouldn't ask again". The in-form mode toggle has
 * been removed from the DOM entirely.
 */
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Store } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api, extractApiError } from "../../lib/api";
import { Card, CardHeader, CardBody, CardFooter } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";

type SignupMode = "participant" | "dealer";

function resolveMode(raw: string | null): { mode: SignupMode; explicit: boolean } {
  if (!raw) return { mode: "participant", explicit: false };
  const lc = raw.toLowerCase();
  if (lc === "vendor" || lc === "dealer") return { mode: "dealer", explicit: true };
  // Treat any other value as "participant, but the user navigated here on purpose".
  return { mode: "participant", explicit: true };
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { mode, explicit } = resolveMode(params.get("as"));
  const isVendor = mode === "dealer";

  const [form, setForm] = React.useState({
    email: "",
    username: "",
    full_name: "",
    password: "",
  });
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Informational only — current Phase-10 default. Live price is enforced
  // server-side on the Stripe Checkout call.
  const DEFAULT_PRICE = 29;

  const update =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register(form);

      if (isVendor) {
        // Account exists as PARTICIPANT (backend ignores any role on signup);
        // now route through Stripe to upgrade.
        try {
          const r = await api.post("/stripe/create-subscription-checkout", {
            origin_url: window.location.origin,
          });
          window.location.href = r.data.url;
          return;
        } catch (subErr) {
          setError(
            extractApiError(subErr) +
              " — your account is created; you can upgrade from the Dashboard.",
          );
          navigate("/profile", { replace: true });
          return;
        }
      }

      navigate("/", { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Per-role copy
  // -----------------------------------------------------------------------
  const headline = isVendor ? "Sign up as a Vendor" : "Sign up as a Participant";
  const lede = isVendor
    ? `Lower platform commission (5%), weekly featured-dealer rotation, public storefront. $${DEFAULT_PRICE}/mo via Stripe.`
    : "Browse, buy, ask the forum, list parts when you've got them spare. Free, 10% commission on anything you sell.";

  return (
    <div className="mx-auto max-w-md px-4 py-10 md:py-16">
      <div className="flex justify-center mb-5" data-testid="register-logo">
        <img
          src="/logo.png"
          alt="Parts Buoy"
          className="h-16 w-16 md:h-20 md:w-20 object-contain"
        />
      </div>
      <Card elevated data-testid="register-card">
        <CardHeader>
          <div>
            <p
              className="text-xs uppercase tracking-[0.18em] font-bold text-brand mb-1"
              data-testid="register-mode-tag"
            >
              {isVendor ? "Vendor signup" : "Participant signup"}
            </p>
            <h1
              className="text-3xl font-black mb-1"
              data-testid="register-headline"
            >
              {headline}
            </h1>
            <p className="text-ink-muted text-sm">{lede}</p>
          </div>
        </CardHeader>
        <CardBody>
          {/* Cross-link to vendor flow when we landed on /register with no `as`. */}
          {!explicit && (
            <Link
              to="/register?as=vendor"
              data-testid="register-cross-link-vendor"
              className="mb-5 flex items-center gap-3 rounded-xl border-2 border-surface-border bg-surface-sunken px-4 py-3 hover:border-ink transition-colors"
            >
              <Store className="h-5 w-5 text-brand shrink-0" aria-hidden />
              <div className="flex-1 text-sm">
                <span className="font-semibold text-ink">Selling parts commercially? </span>
                <span className="text-ink-muted">Sign up as a Vendor →</span>
              </div>
            </Link>
          )}

          {/* Reverse cross-link when on the vendor flow, in case the user
              landed here by mistake. */}
          {isVendor && (
            <Link
              to="/register?as=participant"
              data-testid="register-cross-link-participant"
              className="mb-5 flex items-center gap-3 rounded-xl border-2 border-surface-border bg-surface-sunken px-4 py-3 hover:border-ink transition-colors"
            >
              <div className="flex-1 text-sm">
                <span className="font-semibold text-ink">Just buying or chatting? </span>
                <span className="text-ink-muted">Sign up as a Participant →</span>
              </div>
            </Link>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="register-email"
            />
            <Input
              label="Username"
              type="text"
              autoComplete="username"
              required
              minLength={3}
              maxLength={40}
              value={form.username}
              onChange={update("username")}
              data-testid="register-username"
              hint="Choose a unique handle. Letters, numbers, and dashes work well."
            />
            <Input
              label="Full name (optional)"
              type="text"
              autoComplete="name"
              value={form.full_name}
              onChange={update("full_name")}
              data-testid="register-fullname"
            />
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={update("password")}
              hint="At least 8 characters, including a letter and a number."
              data-testid="register-password"
            />

            {isVendor && (
              <div
                data-testid="register-dealer-note"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm"
              >
                After creating your account we'll route you to Stripe to start
                your Vendor subscription. You can cancel from your dashboard at
                any time.
              </div>
            )}

            {error && (
              <div
                role="alert"
                data-testid="register-error"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              data-testid="register-submit"
            >
              {isVendor ? "Create account & subscribe" : "Create account"}
            </Button>
          </form>
        </CardBody>
        <CardFooter className="justify-between text-sm">
          <span className="text-ink-muted">Already have an account?</span>
          <Link
            to="/login"
            className="font-semibold text-brand hover:text-brand-hover"
            data-testid="register-login-link"
          >
            Sign in →
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
